import type { App, WatchSource } from 'vue'
import { computed, ref, unref, watch } from 'vue'
import {
  configProviderContextKey,
  ElDialog,
  ElDrawer,
  ElLoading,
  ElMessage,
  ElMessageBox,
  ElNotification,
  provideGlobalConfig,
  useZIndex,
} from 'element-plus'

export const ELEMENT_PLUS_OVERLAY_Z_INDEX = 20000

interface OverlayComponent {
  props?: Record<string, unknown>
}

function defaultAppendToBody(component: OverlayComponent) {
  if (!component.props) {
    return
  }

  component.props.appendToBody = {
    type: Boolean,
    default: true,
  }
}

/**
 * Keep every PC overlay in the document body so nested dialogs share one
 * Element Plus z-index stack instead of being trapped by page stacking contexts.
 */
export function configureElementPlusOverlays(app?: App) {
  defaultAppendToBody(ElDialog as OverlayComponent)
  defaultAppendToBody(ElDrawer as OverlayComponent)

  if (!app) return

  // A page's ConfigProvider does not reach programmatic services mounted in
  // separate Vue roots. Give the app and those services the same base instead.
  const previousConfig = app._context.provides[configProviderContextKey as symbol]
  if (unref(previousConfig)?.zIndex !== ELEMENT_PLUS_OVERLAY_Z_INDEX) {
    provideGlobalConfig(computed(() => ({
      ...unref(previousConfig),
      zIndex: ELEMENT_PLUS_OVERLAY_Z_INDEX,
    })), app, true)
  }
  app.use(ElMessage)
  app.use(ElMessageBox)
  app.use(ElNotification)
  app.use(ElLoading)
}

/** Allocate a fresh position in the shared stack whenever a custom overlay opens. */
export function useElementPlusOverlayZIndex(visible: WatchSource<boolean>) {
  const overlayZIndex = ref(0)
  const { nextZIndex } = useZIndex(ELEMENT_PLUS_OVERLAY_Z_INDEX)
  watch(visible, (isVisible) => {
    if (isVisible) overlayZIndex.value = nextZIndex()
  }, { immediate: true, flush: 'sync' })
  return overlayZIndex
}
